import { useCallback, useState } from "react";
import { useListenCallback } from "@hooks/useListenCallback"

export const isElementOverflown = (element: HTMLElement, threshold: number = 0): boolean => {
    return element.scrollWidth > (element.clientWidth + threshold);
}

export const useIsOverflown = (ref: React.RefObject<HTMLElement>, threshold: number = 0) => {
    const [isOverflown, setIsOverflown] = useState(false);
    useListenCallback("resize", () => {
        if (ref.current) {
            setIsOverflown(isElementOverflown(ref.current, threshold));
        }
    });
    return isOverflown;
};

export const useVisibleCards = (ref: React.RefObject<HTMLElement>, size: number, maxContentWidth:number = Infinity) => {
    const [visibleCards, setVisibleCards] = useState(1);
    useListenCallback("resize", () => {
        if (ref.current){
            const sliderNode = ref.current;
            // in case of grid you should use columnGap or for vertical slide rowGap, don't want to make it too complicated for now
            const gap: number = parseFloat(window.getComputedStyle(sliderNode).gap) || 0;
            const width = Math.min(sliderNode.clientWidth, maxContentWidth);
            if(sliderNode.children.length === 0) {
                setVisibleCards(0);
                return;
            }
            const slideNode = sliderNode.children?.[0] as HTMLElement;
            const cardWidth: number = slideNode?.clientWidth + gap;
            setVisibleCards(Math.floor(width / cardWidth));
        }
    }, [size]);
    return visibleCards;
};

/**
 * 
 * @param ref reference to slider node
 * @param size size of elements
 * @param maxContentWidth 
 * @param overflowVisibilityTreshold ignore hidden N pixels, if needed. Default 0.
 * @returns 
 */
export const useSliderControls = (ref: React.RefObject<HTMLElement>, size: number, maxContentWidth: number = Infinity, overflowVisibilityTreshold: number = 0) => {
    const [slideIndex, setSlideIndex] = useState(0);
    const visibleCards = useVisibleCards(ref, size, maxContentWidth);
    const isOverflown = useIsOverflown(ref, overflowVisibilityTreshold);

    const handlePrev = useCallback(() => setSlideIndex((x) => Math.max(0, x - 1)), [setSlideIndex]);
    const handleNext = useCallback(() => setSlideIndex((x) => Math.min(size - visibleCards, x + 1)), [setSlideIndex, size, visibleCards]);
    return {
        isSlidesOverflown: isOverflown,
        slideIndex,
        visibleCards,
        isPreviousEnabled: slideIndex > 0,
        isNextEnabled: (slideIndex + visibleCards) < size, 
        setSlideIndex,
        handlePrev,
        handleNext,
    };
}